import HttpService from "./http-service";
import CitizenInformationRequest, {
  CitizenInformationAttachment,
  CitizenInformationRequestResponse,
  CitizenInformationType,
  CitizenInformationStatus,
} from "@/entities/citizen-information-request";
import AsyncPaginatedRequest from "./async-paginated-request";
import { LazyAsyncIter } from "@/iterators";
import { pageMapperBuilder, Paginated } from "@/pagination";
import { SnakeCasedProperties, SnakeCasedPropertiesDeep } from "type-fest";
import InformationRequestScore from "../entities/information-request-score";
import InformationRequestEntitesScore from "@/entities/information-request-entities-score";

/**
 *  Serviced to CRUD Citizen Information Requests
 */
export default class CitizenInformationRequestService extends HttpService {
  public getAllCitizenInformationRequest(
    params: SearchInformationRequestParams = {}
  ): AsyncIterator<Paginated<CitizenInformationRequest>> {
    const asyncReq = new AsyncPaginatedRequest<CitizenInformationRequestDTO>(
      "get",
      "citizen-information-request",
      params,
      undefined,
      this.httpClient
    )[Symbol.asyncIterator]();

    return new LazyAsyncIter(asyncReq).map(
      pageMapperBuilder(CitizenInformationRequestFromDTO)
    );
  }

  /**
   * Get information request
   */

  public async getInformationRequest(
    requestId: number
  ): Promise<CitizenInformationRequest> {
    const { data } = await this.httpClient.get<CitizenInformationRequestDTO>(
      `citizen-information-request/${requestId}`
    );
    return CitizenInformationRequestFromDTO(data);
  }

  /**
   *  Create a new information request
   */
  public async insertInformationRequest(
    request: CreateInformationRequest
  ): Promise<number> {
    let reqData: CreateInformationRequestDTO | FormData = {
      ...request,
    };
    // If we have new attachments convert request data to FormData
    if (request.newAttachments) {
      reqData = this.createFormData(request);
    }
    const { data } = await this.httpClient.post(
      `/citizen-information-request`,
      reqData,
      this.setHeaders(reqData)
    );
    return data;
  }

  /**
   * Update an information request
   */

  public async updateInformationRequest(
    request: UpdateInformationRequest
  ): Promise<void> {
    let reqData: UpdateInformationRequestDTO | FormData = {
      id: request.id,
      subject: request.subject,
      description: request.description,
      type_request_id: request.type_request_id,
      user_id: request.user_id,
      entity_id: request.entity_id,
      status_id: request.status_id,
      remove_attachments: undefined,
      new_attachments: undefined,
    };

    reqData = this.createFormData(request) as FormData;
    // PHP doesn't parse Form Data when the request method is not POST. We use method
    // spoofing to hit the PUT route with POST
    let method: "put" | "post" = "put";
    if (reqData instanceof FormData) {
      reqData.append("_method", "PUT");
      method = "post";
    }

    await this.httpClient[method](
      `/citizen-information-request/${request.id}`,
      reqData,
      this.setHeaders(reqData)
    );
  }

  /**
   * Delete an information request
   */

  public async deleteInformationRequest(informationId: number): Promise<void> {
    await this.httpClient.delete(
      `/citizen-information-request/${informationId}`
    );
  }

  /**
   *  Create a new information request
   */
  public async updateInformationRequestStatus(
    requestId: number
  ): Promise<void> {
    const { data } = await this.httpClient.put(
      `/status/update/citizen-information-request/${requestId}`
    );
    return data;
  }

  /**
   * Get all scores by entities (only entities with rates)
   */
  // public async getEntitiesScore(){

  // }

  // public getEntitiesScore(

  // ): AsyncIterator<Paginated<InformationRequestEntitesScore>> {

  //   const asyncReq = new AsyncPaginatedRequest<InformationRequestEntitesScoreDTO>(
  //     "get",
  //     "citizen-info-req-score",
  //     null,
  //     undefined,
  //     this.httpClient
  //   )[Symbol.asyncIterator]();

  //   return new LazyAsyncIter(asyncReq).map(
  //     pageMapperBuilder(InformationRequestEntitesScoreFromDTO)
  //   );
  // }

  public async getEntitiesScore(): Promise<InformationRequestEntitesScoreDTO> {
    const { data } = await this.httpClient.get("citizen-info-req-score");
    return data;
  }

  /**
   * Get score of information
   */

  public async getInformationRequestScore(
    requestId: number
  ): Promise<InformationRequestScore> {
    const { data } = await this.httpClient.get<InformationRequestScoreDTO>(
      `/citizen-info-req-score/${requestId}`
    );
    return InformationRequestScoreFromDTO(data);
  }

  /**
   * Insert score of information request quality
   */

  public async insertInformationRequestScore(
    score: Omit<InformationRequestScoreDTO, "id" | "entity_id">
  ): Promise<void> {
    const { data } = await this.httpClient.post(
      `/citizen-info-req-score`,
      score
    );
    return data;
  }

  private setHeaders(
    data: CreateUpdateInformationRequestDTO
  ): { headers: Record<string, string> } | undefined {
    if (data instanceof FormData) {
      return {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };
    }
  }

  private createFormData(
    request: UpdateInformationRequest | CreateInformationRequest
  ): CreateUpdateInformationRequestDTO {
    const reqData: CreateInformationRequestDTO | UpdateInformationRequestDTO = {
      subject: request.subject,
      description: request.description,
      type_request_id: request.type_request_id,
      user_id: request.user_id,
      entity_id: request.entity_id,
      new_attachments: undefined,
      remove_attachments: (request as UpdateInformationRequest)
        .removeAttachments,
    };
    // If we have new attachments, convert request data to FormData
    if (request.newAttachments) {
      reqData.new_attachments = request.newAttachments;
      const formData = new FormData();
      Object.entries(reqData).forEach(([name, value]) => {
        if (value !== undefined) {
          if (Array.isArray(value)) {
            if (name === "new_attachments") {
              (value as File[]).forEach((file) =>
                formData.append("new_attachments[]", file)
              );
            } else if (name === "remove_attachments") {
              (value as number[]).forEach((id) =>
                formData.append("remove_attachments[]", id.toString())
              );
            }
          } else {
            formData.append(name, value?.toString());
          }
        }
      });
      return formData;
    }
    return reqData;
  }
}

export type UpdateInformationRequest = Omit<
  CitizenInformationRequest,
  "attachments"
> & {
  /**
   * Ids of attachments to delete.
   */
  removeAttachments?: number[];
  newAttachments?: File[];
};
export type CreateInformationRequest = Omit<
  UpdateInformationRequest,
  "id" | "removeAttachments"
>;
type UpdateInformationRequestDTO =
  SnakeCasedProperties<UpdateInformationRequest>;
type CreateInformationRequestDTO =
  SnakeCasedProperties<CreateInformationRequest>;
type CreateUpdateInformationRequestDTO =
  | UpdateInformationRequestDTO
  | FormData
  | CreateInformationRequestDTO;

export type CitizenInformationRequestDTO =
  SnakeCasedPropertiesDeep<CitizenInformationRequest>;
export type CitizenInformationRequestAttachmentDTO =
  SnakeCasedProperties<CitizenInformationAttachment>;

export type CitizenInformationRequestResponseDTO =
  SnakeCasedProperties<CitizenInformationRequestResponse>;

// handle status and type

export type CitizenInformationTypeDTO =
  SnakeCasedProperties<CitizenInformationType>;

export type CitizenInformationStatusDTO =
  SnakeCasedProperties<CitizenInformationStatus>;

export function CitizenInformationRequestFromDTO(
  dto: CitizenInformationRequestDTO
): CitizenInformationRequest {
  return new CitizenInformationRequest({
    id: dto.id,
    subject: dto.subject,
    description: dto.description,
    type_request_id: dto.type_request_id,
    user_id: dto.user_id,
    entity_id: dto.entity_id,
    status_id: dto.status_id,
    gender: dto.gender,
    attachments: dto.attachments.map(CitizenInformationAttachmentFromDTO),
    info_response:
      dto.info_response == undefined
        ? undefined
        : CitizenInformationRequestResponseFromDTO(dto.info_response),
    created_at: dto.created_at,
    deleted_at: dto.deleted_at,
    user: dto.user,
    type_request:
      dto.type_request == undefined
        ? undefined
        : CitizenInformationTypeFromDTO(dto.type_request),
    status:
      dto.status == undefined
        ? undefined
        : CitizenInformationStatusFromDTO(dto.status),
  });
}

export function CitizenInformationAttachmentFromDTO(
  dto: CitizenInformationRequestAttachmentDTO
): CitizenInformationAttachment {
  return {
    id: dto.id,
    name: dto.name,
    download_url: dto.download_url,
  };
}

export function CitizenInformationRequestResponseFromDTO(
  dto: CitizenInformationRequestResponseDTO
): CitizenInformationRequestResponse {
  return {
    id: dto.id,
    description: dto.description,
    attachments: dto.attachments.map(CitizenInformationAttachmentFromDTO),
    created_at: dto.created_at,
    updated_at: dto.updated_at,
    status_id: dto.status_id,
  };
}

export function CitizenInformationTypeFromDTO(
  dto: CitizenInformationTypeDTO
): CitizenInformationType {
  return {
    id: dto.id,
    name: dto.name,
  };
}

export function CitizenInformationStatusFromDTO(
  dto: CitizenInformationStatusDTO
): CitizenInformationStatus {
  return {
    id: dto.id,
    name: dto.name,
  };
}

/**
 *  DTO
 */

export interface InformationRequestScoreDTO {
  id: number;
  score: number;
  citizen_request_id: number;
  entity_id?: number;
}

export function InformationRequestScoreFromDTO(
  dto: InformationRequestScoreDTO
): InformationRequestScore {
  return new InformationRequestScore({
    id: dto.id,
    score: dto.score,
    citizen_request_id: dto.citizen_request_id,
    entity_id: dto.entity_id,
  });
}

export interface InformationRequestEntitesScoreDTO {
  entity_id: number;
  name: string;
  avg_score: number;
}

export function InformationRequestEntitesScoreFromDTO(
  dto: InformationRequestEntitesScore
): InformationRequestEntitesScore {
  return new InformationRequestEntitesScore({
    entity_id: dto.entity_id,
    name: dto.name,
    avg_score: dto.avg_score,
  });
}

export interface SearchInformationRequestParams {
  request_subject?: string;
  all?: boolean;
  type_request_ids?: number[];
  status_id?: number;
  deleted?: boolean;
}
